import { motion } from "framer-motion";
import { PlusIcon } from "@heroicons/react/24/outline";

import { Wallet } from "../../../services/queries/wallets/types";
import { CreatableWallet } from "../../../utils/creatableWallets";
import { formatMoney } from "../../../utils/money";
import Flag from "../../Flag";
import CreateWalletTile from "../CreateWalletTile";

interface WalletRailProps {
  wallets: Wallet[];
  missing: CreatableWallet[];
  creatingCurrency?: string | null | undefined;
  onAdd: (walletId: string, currency: string) => void;
  onCreate: (wallet: CreatableWallet) => void;
}

function WalletMiniCard({
  wallet,
  onAdd,
}: {
  wallet: Wallet;
  onAdd: (walletId: string, currency: string) => void;
}) {
  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 260, damping: 20 }}
      className="flex w-40 shrink-0 flex-col justify-between rounded-xl bg-white p-4 shadow-sm"
    >
      <div className="flex items-center justify-between">
        <Flag currency={wallet.currencyCode} className="h-6 w-6" />
        <span className="text-xs font-medium text-gray-400">
          {wallet.currencyCode}
        </span>
      </div>

      <div className="mt-4">
        <div className="text-lg font-semibold text-gray-900">
          {formatMoney(wallet.availableBalance, wallet.currencySymbol)}
        </div>
        <div className="text-[11px] text-gray-400">Available</div>
      </div>

      {wallet.canDeposit && (
        <button
          type="button"
          onClick={() => onAdd(wallet.id, wallet.currencyCode)}
          className="mt-3 inline-flex items-center gap-1 self-start rounded-lg bg-primary-50 px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-primary-100 transition-colors"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          Add money
        </button>
      )}
    </motion.div>
  );
}

export default function WalletRail({
  wallets,
  missing,
  creatingCurrency,
  onAdd,
  onCreate,
}: WalletRailProps) {
  if (wallets.length === 0 && missing.length === 0) return null;

  return (
    <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
      {wallets.map((wallet) => (
        <WalletMiniCard key={wallet.id} wallet={wallet} onAdd={onAdd} />
      ))}
      {missing.map((wallet) => (
        <CreateWalletTile
          key={wallet.currency}
          wallet={wallet}
          creating={creatingCurrency === wallet.currency}
          onCreate={onCreate}
        />
      ))}
    </div>
  );
}
